// components/clients/ClientDetailView.js
// Comprehensive Account Management Dashboard for AM/Customer Success teams

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, DollarSign, Clock, TrendingUp,
  AlertTriangle, CheckCircle, Calendar, FileText, MessageSquare, Users,
  BarChart3, Target, Star, Bell, Download, Plus, Edit2, Eye,
  ChevronDown, ChevronRight, Activity, Briefcase, Flag, Award, Send,
  ClipboardList, ThumbsUp, AlertCircle, TrendingDown, ExternalLink,
  Settings, Archive, RefreshCw, Search, X, PlayCircle, Paperclip, User
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ClientSurvey from './ClientSurvey';
import ClientNotes from './ClientNotes';

const ClientDetailView = ({
  client,
  projects,
  timeEntries,
  currentTenant, 
  user, 
  teamMembers = [],
  onBack,
  onEditClient,
  formatTime,
  calculateProjectHealth
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showQBR, setShowQBR] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  const [showNotes, setShowNotes] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);

  // Calculate comprehensive client metrics
  const clientMetrics = useMemo(() => {
    // Time metrics
    const totalHours = timeEntries.reduce((sum, e) => sum + (e.duration_hours || 0), 0);
    const billableHours = timeEntries.filter(e => e.is_billable)
      .reduce((sum, e) => sum + (e.duration_hours || 0), 0);
    
    // Financial metrics
    const totalRevenue = timeEntries.filter(e => e.is_billable)
      .reduce((sum, e) => sum + ((e.duration_hours || 0) * (e.hourly_rate || 0)), 0);
    const avgRate = billableHours > 0 ? totalRevenue / billableHours : 0;
    
    // Project health
    const activeProjects = projects.filter(p => p.status === 'active');
    const projectsWithHealth = activeProjects.map(project => {
      const projectHours = timeEntries
        .filter(e => e.project_id === project.id)
        .reduce((sum, e) => sum + (e.duration_hours || 0), 0);
      return {
        ...project,
        hours: projectHours,
        health: calculateProjectHealth(project, projectHours)
      };
    });
    
    const healthyProjects = projectsWithHealth.filter(p => p.health.status === 'healthy').length;
    const atRiskProjects = projectsWithHealth.filter(p => p.health.status === 'warning').length;
    const criticalProjects = projectsWithHealth.filter(p => p.health.status === 'critical').length;
    
    // Team engagement
    const uniqueTeamMembers = new Set(timeEntries.map(e => e.user_id)).size;
    
    // Recent activity
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentEntries = timeEntries.filter(e => 
      new Date(e.entry_date) >= thirtyDaysAgo
    );
    const recentHours = recentEntries.reduce((sum, e) => sum + (e.duration_hours || 0), 0);
    
    // Budget tracking
    const totalBudgetHours = projects.reduce((sum, p) => sum + (p.budget_hours || 0), 0);
    const totalBudgetAmount = projects.reduce((sum, p) => sum + (p.budget_amount || 0), 0);
    const budgetUtilization = totalBudgetHours > 0 ? (totalHours / totalBudgetHours) * 100 : 0;
    
    return {
      totalHours,
      billableHours,
      totalRevenue,
      avgRate,
      projectCount: projects.length,
      activeProjects: activeProjects.length,
      healthyProjects,
      atRiskProjects,
      criticalProjects,
      uniqueTeamMembers,
      recentHours,
      totalBudgetHours,
      totalBudgetAmount,
      budgetUtilization,
      projectsWithHealth,
      utilizationRate: totalHours > 0 ? (billableHours / totalHours) * 100 : 0
    };
  }, [projects, timeEntries, calculateProjectHealth]);

  // Load notes
  const loadNotes = useCallback(async () => {
    if (!client?.id) return;
    
    try {
      setLoadingNotes(true);
      const { data, error } = await supabase
        .from('client_notes')
        .select(`
          *,
          created_by_user:created_by (full_name, email),
          attachments:client_note_attachments (*)
        `)
        .eq('client_id', client.id)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setNotes(data || []);
    } catch (err) {
      console.error('Error loading notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  }, [client?.id]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Activity timeline
  const activityTimeline = useMemo(() => {
    return timeEntries
      .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
      .slice(0, 10)
      .map(entry => ({
        ...entry,
        type: 'time_entry',
        icon: Clock,
        color: 'blue'
      }));
  }, [timeEntries]);

  // Top performers
  const topPerformers = useMemo(() => {
    const userStats = {};
    timeEntries.forEach(entry => {
      if (!userStats[entry.user_id]) {
        userStats[entry.user_id] = {
          userId: entry.user_id,
          userName: entry.user_name || 'Unknown',
          hours: 0,
          revenue: 0,
          entries: 0
        };
      }
      userStats[entry.user_id].hours += entry.duration_hours || 0;
      if (entry.is_billable) {
        userStats[entry.user_id].revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0);
      }
      userStats[entry.user_id].entries += 1;
    });
    
    return Object.values(userStats)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 5);
  }, [timeEntries]);

  // Note type configuration
  const noteTypes = [
    { value: 'general', label: 'General Note', color: 'blue', icon: MessageSquare },
    { value: 'meeting', label: 'Meeting', color: 'purple', icon: Calendar },
    { value: 'call', label: 'Call Log', color: 'green', icon: Calendar },
    { value: 'email', label: 'Email', color: 'orange', icon: MessageSquare },
    { value: 'achievement', label: 'Achievement', color: 'yellow', icon: MessageSquare },
    { value: 'issue', label: 'Issue', color: 'red', icon: AlertCircle },
    { value: 'followup', label: 'Follow-up', color: 'indigo', icon: MessageSquare }
  ];

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return FileText;
    if (fileType?.startsWith('video/')) return FileText;
    if (fileType?.includes('pdf')) return FileText;
    return FileText;
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-attachments')
        .download(attachment.file_path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  // Metric Card Component
  const MetricCard = ({ icon: Icon, label, value, subtitle, trend, color = 'blue' }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend && (
          <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-slate-900 mb-1">{value}</div>
      <div className="text-sm text-slate-600">{label}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );

  // Health Score Component
  const HealthScore = ({ score, label }) => {
    const getColor = () => {
      if (score >= 80) return 'green';
      if (score >= 60) return 'yellow';
      return 'red';
    };
    const color = getColor();
    
    return (
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-2">
          <svg className="transform -rotate-90 w-24 h-24">
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              className="text-slate-200"
            />
            <circle
              cx="48"
              cy="48"
              r="40"
              stroke="currentColor"
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={`${2 * Math.PI * 40}`}
              strokeDashoffset={`${2 * Math.PI * 40 * (1 - score / 100)}`}
              className={`text-${color}-500`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-slate-900">{score}</span>
          </div>
        </div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: client.color }}
                />
                <h1 className="text-3xl font-bold text-slate-900">{client.name}</h1>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                {client.contact_email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${client.contact_email}`} className="hover:text-blue-600">
                      {client.contact_email}
                    </a>
                  </div>
                )}
                {client.contact_phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>{client.contact_phone}</span>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{client.address}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowQBR(true)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>QBR</span>
            </button>
            <button
              onClick={() => setShowSurvey(true)}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center space-x-2"
            >
              <ClipboardList className="w-4 h-4" />
              <span>Survey</span>
            </button>
            <button
              onClick={() => onEditClient(client)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit Client</span>
            </button>
          </div>
        </div>

        {/* Health Score Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
          <HealthScore 
            score={Math.round((clientMetrics.healthyProjects / Math.max(clientMetrics.activeProjects, 1)) * 100)} 
            label="Project Health"
          />
          <HealthScore 
            score={Math.round(clientMetrics.utilizationRate)} 
            label="Utilization"
          />
          <HealthScore 
            score={Math.min(Math.round(clientMetrics.budgetUtilization), 100)} 
            label="Budget Usage"
          />
          <HealthScore 
            score={85} 
            label="Satisfaction"
          />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={DollarSign}
          label="Total Revenue"
          value={`$${clientMetrics.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
          subtitle={`$${clientMetrics.avgRate.toFixed(0)}/hr avg rate`}
          color="green"
        />
        <MetricCard
          icon={Clock}
          label="Total Hours"
          value={formatTime(clientMetrics.totalHours)}
          subtitle={`${clientMetrics.billableHours.toFixed(1)}h billable`}
          color="blue"
        />
        <MetricCard
          icon={Briefcase}
          label="Active Projects"
          value={clientMetrics.activeProjects}
          subtitle={`${clientMetrics.projectCount} total projects`}
          color="purple"
        />
        <MetricCard
          icon={Users}
          label="Team Members"
          value={clientMetrics.uniqueTeamMembers}
          subtitle="Working on account"
          color="orange"
        />
      </div>

      {/* Project Health Overview - keeping existing code */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
          <Target className="w-5 h-5 text-blue-600" />
          <span>Project Health Overview</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold text-green-600">{clientMetrics.healthyProjects}</span>
            </div>
            <div className="text-sm font-medium text-green-900">Healthy Projects</div>
            <div className="text-xs text-green-700 mt-1">On track and performing well</div>
          </div>
          
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="text-2xl font-bold text-yellow-600">{clientMetrics.atRiskProjects}</span>
            </div>
            <div className="text-sm font-medium text-yellow-900">At Risk</div>
            <div className="text-xs text-yellow-700 mt-1">Needs attention soon</div>
          </div>
          
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-2xl font-bold text-red-600">{clientMetrics.criticalProjects}</span>
            </div>
            <div className="text-sm font-medium text-red-900">Critical</div>
            <div className="text-xs text-red-700 mt-1">Immediate action required</div>
          </div>
        </div>

        {/* Project List */}
        <div className="space-y-3">
          {clientMetrics.projectsWithHealth.map(project => (
            <div 
              key={project.id}
              className="p-4 border border-slate-200 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    <h4 className="font-medium text-slate-900">{project.name}</h4>
                    <span className={`px-2 py-1 rounded text-xs font-medium bg-${project.health.color}-100 text-${project.health.color}-700`}>
                      {project.health.label}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-6 text-sm text-slate-600">
                    <span>{project.hours.toFixed(1)}h tracked</span>
                    {project.budget_hours && (
                      <span className="flex items-center space-x-1">
                        <Target className="w-4 h-4" />
                        <span>{((project.hours / project.budget_hours) * 100).toFixed(0)}% of budget</span>
                      </span>
                    )}
                    {project.priority && (
                      <span className="flex items-center space-x-1">
                        <Flag className="w-4 h-4" />
                        <span className="capitalize">{project.priority}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {project.budget_hours && (
                <div className="mt-3">
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className={`h-full rounded-full ${
                        project.health.status === 'critical' ? 'bg-red-500' :
                        project.health.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min((project.hours / project.budget_hours) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Two Column Layout - keeping existing code */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
            <Award className="w-5 h-5 text-yellow-600" />
            <span>Top Performers</span>
          </h3>
          
          <div className="space-y-3">
            {topPerformers.length > 0 ? topPerformers.map((performer, index) => (
              <div key={performer.userId} className="flex items-center space-x-4 p-3 bg-slate-50 rounded-lg">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-400 text-yellow-900' :
                  index === 1 ? 'bg-slate-300 text-slate-700' :
                  index === 2 ? 'bg-amber-600 text-white' :
                  'bg-slate-200 text-slate-600'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{performer.userName}</div>
                  <div className="text-sm text-slate-600">
                    {performer.hours.toFixed(1)}h • ${performer.revenue.toFixed(0)} revenue
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-slate-900">{performer.entries}</div>
                  <div className="text-xs text-slate-500">entries</div>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-slate-500">
                No time entries yet
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5 text-green-600" />
            <span>Recent Activity</span>
          </h3>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {activityTimeline.length > 0 ? activityTimeline.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                <div className={`p-2 rounded-lg bg-${activity.color}-100`}>
                  <activity.icon className={`w-4 h-4 text-${activity.color}-600`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{activity.task_name}</div>
                  <div className="text-sm text-slate-600">
                    {activity.user_name} • {activity.duration_hours?.toFixed(1)}h
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(activity.entry_date).toLocaleDateString()}
                  </div>
                </div>
                {activity.is_billable && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                    Billable
                  </span>
                )}
              </div>
            )) : (
              <div className="text-center py-8 text-slate-500">
                No recent activity
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Budget Tracking - keeping existing code */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <span>Budget & Financial Tracking</span>
          </h3>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-blue-700 mb-1">Total Budget (Hours)</div>
            <div className="text-2xl font-bold text-blue-900 mb-2">
              {clientMetrics.totalBudgetHours.toFixed(0)}h
            </div>
            <div className="text-sm text-blue-600">
              {clientMetrics.totalHours.toFixed(0)}h used ({clientMetrics.budgetUtilization.toFixed(0)}%)
            </div>
            <div className="mt-3 w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-full rounded-full"
                style={{ width: `${Math.min(clientMetrics.budgetUtilization, 100)}%` }}
              />
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg">
            <div className="text-sm text-green-700 mb-1">Total Budget (Amount)</div>
            <div className="text-2xl font-bold text-green-900 mb-2">
              ${clientMetrics.totalBudgetAmount.toFixed(0)}
            </div>
            <div className="text-sm text-green-600">
              ${clientMetrics.totalRevenue.toFixed(0)} invoiced
            </div>
            <div className="mt-3 w-full bg-green-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-full rounded-full"
                style={{ 
                  width: `${Math.min((clientMetrics.totalRevenue / Math.max(clientMetrics.totalBudgetAmount, 1)) * 100, 100)}%` 
                }}
              />
            </div>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="text-sm text-purple-700 mb-1">Recent Activity</div>
            <div className="text-2xl font-bold text-purple-900 mb-2">
              {clientMetrics.recentHours.toFixed(0)}h
            </div>
            <div className="text-sm text-purple-600">
              Last {timeRange} days
            </div>
          </div>
        </div>
      </div>

      {/* Notes & Communication - UPDATED */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center space-x-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <span>Notes & Communication</span>
          </h3>
          <button
            onClick={() => setShowNotes(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Notes</span>
          </button>
        </div>

        <div className="space-y-3">
          {loadingNotes ? (
            <div className="text-center py-8 text-slate-500">Loading notes...</div>
          ) : notes.length > 0 ? (
            notes.map(note => {
              const typeColors = {
                general: 'blue',
                meeting: 'purple',
                call: 'green',
                email: 'orange',
                achievement: 'yellow',
                issue: 'red',
                followup: 'indigo'
              };
              const color = typeColors[note.note_type] || 'blue';
              
              const typeLabels = {
                general: 'General Note',
                meeting: 'Meeting',
                call: 'Call Log',
                email: 'Email',
                achievement: 'Achievement',
                issue: 'Issue',
                followup: 'Follow-up'
              };
              const typeLabel = typeLabels[note.note_type] || 'Note';

              return (
                <div 
                  key={note.id} 
                  className={`p-4 bg-slate-50 border-l-4 border-${color}-500 rounded-lg hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => setViewingNote(note)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-slate-900">{note.title}</span>
                      <span className={`px-2 py-1 bg-${color}-100 text-${color}-700 text-xs rounded`}>
                        {typeLabel}
                      </span>
                      {note.is_private && (
                        <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded">
                          Private
                        </span>
                      )}
                      {note.attachments && note.attachments.length > 0 && (
                        <span className="flex items-center space-x-1 text-xs text-slate-500">
                          <Paperclip className="w-3 h-3" />
                          <span>{note.attachments.length}</span>
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-slate-500">
                      {new Date(note.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">
                    {note.content.length > 150 
                      ? `${note.content.substring(0, 150)}...` 
                      : note.content
                    }
                  </p>
                  {note.created_by_user && (
                    <div className="text-xs text-slate-500 mt-2">
                      by {note.created_by_user.full_name}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-3">No notes yet for this client</p>
              <button
                onClick={() => setShowNotes(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Add your first note
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Alerts & Action Items - keeping existing code */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
          <Bell className="w-5 h-5 text-orange-600" />
          <span>Alerts & Action Items</span>
        </h3>

        <div className="space-y-3">
          {clientMetrics.criticalProjects > 0 && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-red-900 mb-1">
                  {clientMetrics.criticalProjects} Critical Project{clientMetrics.criticalProjects > 1 ? 's' : ''}
                </div>
                <div className="text-sm text-red-700">
                  Immediate attention required for over-budget projects
                </div>
              </div>
              <button className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">
                Review
              </button>
            </div>
          )}

          {clientMetrics.atRiskProjects > 0 && (
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-yellow-900 mb-1">
                  {clientMetrics.atRiskProjects} Project{clientMetrics.atRiskProjects > 1 ? 's' : ''} At Risk
                </div>
                <div className="text-sm text-yellow-700">
                  Monitor these projects closely to prevent budget overruns
                </div>
              </div>
              <button className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700">
                Monitor
              </button>
            </div>
          )}

          <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-blue-900 mb-1">
                QBR Due Soon
              </div>
              <div className="text-sm text-blue-700">
                Quarterly Business Review scheduled for next month
              </div>
            </div>
            <button 
              onClick={() => setShowQBR(true)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              Prepare
            </button>
          </div>
        </div>
      </div>

      {/* View Note Modal */}
      {viewingNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">
                {viewingNote.title}
              </h3>
              <button onClick={() => setViewingNote(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Note Metadata */}
              <div className="flex items-center space-x-4 text-sm text-slate-600 pb-4 border-b border-slate-200">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(viewingNote.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>{viewingNote.created_by_user?.full_name || 'Unknown'}</span>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium bg-${noteTypes.find(t => t.value === viewingNote.note_type)?.color || 'blue'}-100 text-${noteTypes.find(t => t.value === viewingNote.note_type)?.color || 'blue'}-700`}>
                  {noteTypes.find(t => t.value === viewingNote.note_type)?.label || 'Note'}
                </span>
                {viewingNote.is_private && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-slate-200 text-slate-600">
                    Private
                  </span>
                )}
              </div>

              {/* Note Content */}
              <div className="prose max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">{viewingNote.content}</p>
              </div>

              {/* Attachments */}
              {viewingNote.attachments && viewingNote.attachments.length > 0 && (
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center space-x-2">
                    <Paperclip className="w-4 h-4" />
                    <span>Attachments ({viewingNote.attachments.length})</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {viewingNote.attachments.map((attachment) => {
                      const FileIcon = getFileIcon(attachment.file_type);
                      const isImage = attachment.file_type?.startsWith('image/');
                      
                      return (
                        <div key={attachment.id} className="border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                          {isImage && attachment.file_url && (
                            <img 
                              src={attachment.file_url} 
                              alt={attachment.file_name}
                              className="w-full h-32 object-cover rounded mb-2"
                            />
                          )}
                          <div className="flex items-center space-x-2">
                            <FileIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {attachment.file_name}
                              </p>
                              <p className="text-xs text-slate-500">
                                {(attachment.file_size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                            <div className="flex space-x-1">
                              {attachment.file_url && (
                                <a
                                  href={attachment.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="View"
                                >
                                  <Eye className="w-4 h-4" />
                                </a>
                              )}
                              <button
                                onClick={() => handleDownloadAttachment(attachment)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => {
                    setViewingNote(null);
                    setShowNotes(true);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors flex items-center space-x-2"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit in Notes</span>
                </button>
                <button
                  onClick={() => setViewingNote(null)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QBR Modal - keeping existing code */}
      {showQBR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Quarterly Business Review</h3>
              <button
                onClick={() => setShowQBR(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-blue-700 mb-1">Period</div>
                  <div className="text-xl font-bold text-blue-900">Q4 2024</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-green-700 mb-1">Overall Score</div>
                  <div className="text-xl font-bold text-green-900">8.5/10</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Key Achievements</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Delivered {clientMetrics.projectCount} projects with {clientMetrics.totalHours.toFixed(0)} hours tracked</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{clientMetrics.utilizationRate.toFixed(0)}% utilization rate maintained</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">${clientMetrics.totalRevenue.toFixed(0)} in total revenue generated</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Areas for Improvement</h4>
                <ul className="space-y-2">
                  {clientMetrics.atRiskProjects > 0 && (
                    <li className="flex items-start space-x-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{clientMetrics.atRiskProjects} project(s) require closer monitoring</span>
                    </li>
                  )}
                  <li className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Communication cadence could be more frequent</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Next Quarter Goals</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <Target className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Maintain {clientMetrics.healthyProjects} healthy projects</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Target className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Establish bi-weekly sync meetings</span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowQBR(false)}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  Close
                </button>
                <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                  <Download className="w-4 h-4" />
                  <span>Export Report</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Survey Modal */}
      {showSurvey && (
        <ClientSurvey
          client={client}
          onClose={() => setShowSurvey(false)}
          currentTenant={currentTenant}
          user={user}
        />
      )}

      <ClientNotes
        client={client}
        currentTenant={currentTenant}
        user={user}
        isOpen={showNotes}
        onClose={() => {
          setShowNotes(false);
          loadNotes(); // Refresh notes when modal closes
        }}
      />
    </div>
  );
};

export default ClientDetailView;